import axios from 'axios';
import config from '../../config';
import { persistor, store } from '../../redux/store';
import { logLastAction, logout } from '../../redux/auth/authSlice';
// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.baseURL = config.API_URL;

axios.interceptors.request.use((config) => {
    let bearerToken = store.getState().auth.accessToken;
    store.dispatch(logLastAction());

    if (bearerToken) {
        // @ts-ignore
        config.headers.common['Authorization'] = 'Bearer ' + bearerToken;
    }

    return config;
});

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        if (response?.config?.method !== 'get') store.dispatch(logLastAction());
        return Promise.resolve(response);
    },
    async (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        const status = error?.response?.status;

        if (status === 401 || status === 403) {
            if (store.getState().auth.hasUnacceptedPrivacyStatements) {
                // the api refuses everything until the statement is accepted
                if (window.location.pathname !== '/account/privacyStatements') {
                    window.location.href = '/account/privacyStatements';
                }
            } else {
                // clear the persisted session before leaving, otherwise the
                // login page navigates straight back and the app reloads in a loop
                store.dispatch(logout());
                await persistor.flush();
                if (window.location.pathname !== '/account/login') {
                    window.location.href = '/account/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

class APICore {
    /**
     * Fetches data from given url
     */
    get = (url: string, params?: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                      .map((key) => key + '=' + params[key])
                      .join('&')
                : '';
            response = axios.get(`${url}?${queryString}`, params);
        } else {
            response = axios.get(`${url}`, params);
        }
        return response;
    };

    getFile = (url: string, params: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                      .map((key) => key + '=' + params[key])
                      .join('&')
                : '';
            response = axios.get(`${url}?${queryString}`, { responseType: 'blob' });
        } else {
            response = axios.get(`${url}`, { responseType: 'blob' });
        }
        return response;
    };

    getMultiple = (urls: string, params: any) => {
        const reqs = [];
        let queryString = '';
        if (params) {
            queryString = params
                ? Object.keys(params)
                      .map((key) => key + '=' + params[key])
                      .join('&')
                : '';
        }

        for (const url of urls) {
            reqs.push(axios.get(`${url}?${queryString}`));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any) => {
        return axios.post(url, data);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(url, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const auth = store.getState()?.auth;

        if (!auth || !auth.authenticated || (auth && !auth.accessToken)) {
            return false;
        }
        const currentTime = new Date();
        if (new Date(auth.expiresAt) < currentTime) {
            console.warn('access token expired');
            return false;
        } else {
            return true;
        }
    };
}

export { APICore };
